import classNames from 'classnames'
import { Dialog, DialogContent, DialogOverlay, DialogPortal, DialogTitle } from '@radix-ui/react-dialog'

import { Avatar, Body } from '@foxino/components-web'
import { ChevronRight, LogOut02, Settings04, UserCircle } from '@foxino/icons-react'

import { logout } from '@app/app/auth/actions/authActions'
import type { GetUserProfileQuery } from '@app/data'
import { useTranslation } from '@app/locales'
import { getFullName } from '@app/modules/common/utils'
import { SettingsSection } from '@app/modules/settings/model/enums'
import cancelEvent from '@app/utils/cancelEvent'

type Props = {
  userProfile: GetUserProfileQuery['userProfile']
  onClose: () => void
  onSettingsOpen: (section: SettingsSection) => void
}

export const SmallProfileDialog = ({ onClose, onSettingsOpen, userProfile }: Props) => {
  const { t } = useTranslation('settings')

  const { firstName, lastName, pictureUrl, email } = userProfile

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay
          className="fixed inset-0 z-[50]"
          onClick={(event) => {
            cancelEvent(event)
            onClose()
          }}
        />

        <DialogContent
          className={classNames(
            'fixed bottom-10 left-20 z-[60] flex w-[267px] overflow-x-hidden rounded-2xl border border-primary-600 bg-primary-800 p-[14px]'
          )}
          aria-describedby={undefined}
          onClick={cancelEvent}
        >
          <div className="flex w-full flex-col gap-4">
            <DialogTitle className="hidden">{t('title')}</DialogTitle>
            <div className="flex w-full items-center gap-[10px]">
              <Avatar
                src={pictureUrl ?? undefined}
                showTooltip
                fullName={getFullName(firstName, lastName)}
                className="size-[42px] min-w-[42px] rounded-[10px]"
              />
              <div className="flex flex-1 flex-col overflow-hidden">
                <Body className="truncate text-sm text-neutral-100">{getFullName(firstName, lastName)}</Body>
                <Body className="truncate text-xs text-primary-300">{email}</Body>
              </div>
            </div>

            <div className="flex gap-6">
              <div
                className={classNames(
                  'flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg p-1 hover:bg-primary-900'
                )}
                onClick={() => onSettingsOpen(SettingsSection.PROFILE)}
              >
                <div className="flex items-center gap-[10px]">
                  <UserCircle className="size-4 text-white opacity-50" />
                  <Body size="md" weight="regular" className="truncate text-xs text-white">
                    {t('smallDialog.yourProfile')}
                  </Body>
                </div>
                <ChevronRight className="h-[10px] text-white" />
              </div>
            </div>

            <div className="flex gap-6">
              <div
                className={classNames(
                  'flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg p-1 hover:bg-primary-900'
                )}
                onClick={() => onSettingsOpen(SettingsSection.PREFERENCES)}
              >
                <div className="flex items-center gap-[10px]">
                  <Settings04 className="size-4 text-white opacity-50" />
                  <Body size="md" weight="regular" className="truncate text-xs text-white">
                    {t('smallDialog.preferences')}
                  </Body>
                </div>
                <ChevronRight className="h-3 text-white" />
              </div>
            </div>

            <button
              className="flex items-center gap-2 rounded-lg bg-primary-700 px-3 py-2 hover:bg-primary-600"
              onClick={logout}
            >
              <LogOut02 className="size-3 text-error-600" />
              <Body size="md" weight="regular" className="truncate text-xs text-white">
                {t('smallDialog.logout')}
              </Body>
            </button>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
